import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, AlertTriangle, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { getErrorMessage } from "@/lib/errors";
import { cn } from "@/lib/utils";

const inrShort = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

export default function ProcurementPipeline() {
  const pf = useProjectFilter();
  const navigate = useNavigate();
  const [flow, setFlow] = useState("materials");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stuckOnly, setStuckOnly] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { flow };
      if (pf.value !== "all" && pf.value !== "none") params.project_id = pf.value;
      const { data: res } = await api.get("/procurement/pipeline", { params });
      setData(res);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [flow, pf.value]);

  const stages = (data?.stages || []).map((s) => ({
    ...s,
    shown: pf.value === "none" ? s.cards.filter((c) => !c.project_id) : s.cards,
  })).map((s) => ({ ...s, shown: stuckOnly ? s.shown.filter((c) => c.stuck) : s.shown }));

  return (
    <div className="space-y-5" data-testid="procurement-pipeline-page">
      <PageHeader
        title="Procurement Pipeline"
        description="Everything being bought or subcontracted, by the stage it is waiting at. Red cards have waited longer than normal for that stage — click a card to open its page."
        action={<div className="flex flex-wrap items-center justify-end gap-2">
          <ProjectFilter filter={pf} />
          <Button variant="outline" onClick={load} disabled={loading}><RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} /> Refresh</Button>
        </div>}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant={flow === "materials" ? "default" : "outline"} onClick={() => setFlow("materials")} data-testid="pipeline-materials">Materials (PO flow)</Button>
        <Button size="sm" variant={flow === "subcontract" ? "default" : "outline"} onClick={() => setFlow("subcontract")} data-testid="pipeline-subcontract">Subcontract (WO flow)</Button>
        <label className="ml-2 flex items-center gap-2 text-sm text-muted-foreground">
          <input type="checkbox" checked={stuckOnly} onChange={(e) => setStuckOnly(e.target.checked)} /> Show only stuck items
        </label>
        {data && data.stuck_total > 0 && (
          <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-rose-500/10 px-3 py-1 text-xs font-medium text-rose-600 dark:text-rose-400">
            <AlertTriangle className="h-3.5 w-3.5" /> {data.stuck_total} item(s) stuck
          </span>
        )}
      </div>

      <div className="overflow-x-auto pb-2">
        <div className="flex min-w-max gap-3">
          {stages.map((s) => (
            <div key={s.key} className="flex w-64 shrink-0 flex-col rounded-xl border border-border bg-muted/30" data-testid={`pipeline-stage-${s.key}`}>
              <div className="border-b border-border p-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-foreground">{s.label}</p>
                  <span className="rounded-full bg-background px-2 py-0.5 text-xs text-muted-foreground">{s.count}</span>
                </div>
                <p className="mt-0.5 font-mono text-xs text-foreground">{inrShort(s.total)}</p>
                <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{s.hint}{s.limit_days ? ` · normal ≤ ${s.limit_days} days` : ""}</p>
                {s.stuck > 0 && <p className="mt-1 text-[11px] font-medium text-rose-600 dark:text-rose-400">{s.stuck} stuck</p>}
              </div>
              <div className="flex max-h-[62vh] flex-col gap-2 overflow-y-auto p-2">
                {s.shown.length === 0 && <p className="p-2 text-center text-xs text-muted-foreground">Nothing here</p>}
                {s.shown.map((c) => (
                  <button
                    type="button"
                    key={`${c.kind}-${c.id}`}
                    onClick={() => navigate(c.link)}
                    className={cn("rounded-lg border bg-card p-2.5 text-left text-xs shadow-sm transition hover:border-foreground/30",
                      c.stuck ? "border-rose-400/60" : "border-border")}
                    data-testid={`pipeline-card-${c.number}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-[11px] text-muted-foreground">{c.number}</span>
                      <span className={cn("inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px]",
                        c.stuck ? "bg-rose-500/10 text-rose-600 dark:text-rose-400" : "bg-muted text-muted-foreground")}>
                        <Clock className="h-3 w-3" />{c.days}d
                      </span>
                    </div>
                    <p className="mt-1 line-clamp-2 text-sm text-foreground">{c.title}</p>
                    {c.vendor && <p className="mt-0.5 truncate text-muted-foreground">{c.vendor}</p>}
                    <div className="mt-1.5 flex items-center justify-between">
                      <span className="text-muted-foreground">{c.status}</span>
                      {Number(c.value) > 0 && <span className="font-mono font-semibold text-foreground">{inrShort(c.value)}</span>}
                    </div>
                    {c.note && <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">{c.note}</p>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
